import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  defaultConfigKiosk,
  defaultConfigKioskColors,
  defaultConfigKioskColorsValidation,
  defaultConfigKioskConfiguration,
  defaultConfigKioskConfigurationValidation,
  defaultConfigKioskImages,
  defaultConfigKioskImagesValidation,
} from '../constants';
import { configKioskDataCast, getConfigKioskParams } from '../helpers';
import {
  IConfigKiosk,
  IConfigKioskColors,
  IConfigKioskColorsValidation,
  IConfigKioskConfiguration,
  IConfigKioskConfigurationValidation,
  IConfigKioskImages,
  IConfigKioskImagesValidation,
  IResponseConfigKiosk,
  TConfigKioskColorField,
} from '../types';
import client from 'src/services/client';
import { IValidation, LoadingStatus } from 'src/types/main';
import { emptyStringValidation, minCharsValidation, validationColors } from 'src/utils/validation';
import logger from '~/services/logger';
import { scrollToErrorElement } from '~/utils/helpers';

class ConfigKioskStore {
  constructor() {
    makeObservable(this);
  }

  @observable colors: IConfigKioskColors = defaultConfigKioskColors;
  @observable images: IConfigKioskImages = defaultConfigKioskImages;
  @observable configuration: IConfigKioskConfiguration = defaultConfigKioskConfiguration;

  @observable loading: LoadingStatus = LoadingStatus.Init;
  @observable isEdit: boolean = false;

  @observable focusField: TConfigKioskColorField | null = null;
  @action setFocusField = (field: TConfigKioskColorField | null) => {
    this.focusField = field;
  };

  @computed get isFocusFieldValue(): string {
    if (this.focusField) return this.colors[this.focusField];
    return '';
  }

  @action changeColors = (value: Partial<IConfigKioskColors>) => {
    this.colors = { ...this.colors, ...value };
    this.isEdit = true;

    // update validation
    if (!this.colorsValidation.isValid) this.setColorsValidation();
  };

  @action changeImages = (value: Partial<IConfigKioskImages>) => {
    this.images = { ...this.images, ...value };
    this.isEdit = true;

    // update validation
    if (!this.imagesValidation.isValid) this.setImagesValidation();
  };

  @action changeTimerStartPicker = (value: IConfigKioskConfiguration['timeStart']) => {
    this.configuration.timeStart = value;
    this.isEdit = true;
  };

  @action changeConfig = (value: Partial<IConfigKioskConfiguration>) => {
    this.configuration = { ...this.configuration, ...value };
    this.isEdit = true;

    // update validation
    if (!this.configurationValidation.isValid) this.setConfigurationValidation();
  };

  private configKiosk: IConfigKiosk = defaultConfigKiosk;
  @action public setData = (data: IResponseConfigKiosk) => {
    this.configKiosk = configKioskDataCast(data);

    this.colors = this.configKiosk.colors;
    this.images = this.configKiosk.images;
    this.configuration = this.configKiosk.configuration;
  };

  @action submit = () => {
    this.setValidations();
    if (!this.isValid) {
      scrollToErrorElement();
      return;
    }

    this.updateSettings();
  };

  private get params() {
    return getConfigKioskParams({ colors: this.colors, images: this.images, configuration: this.configuration }, this.configKiosk);
  }

  @action updateSettings = async () => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.put('/v3/settings/consumer-channels/kiosk', this.params);

      this.configKiosk = { colors: this.colors, images: this.images, configuration: this.configuration };
      this.isEdit = false;
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'configKioskStore updateSettings', showModal: true });
    }
  };

  @computed get isValid(): boolean {
    return this.colorsValidation.isValid && this.imagesValidation.isValid && this.configurationValidation.isValid;
  }

  @action setValidations = () => {
    this.setColorsValidation();
    this.setImagesValidation();
    this.setConfigurationValidation();
  };

  @observable colorsValidation: IConfigKioskColorsValidation = cloneDeep(defaultConfigKioskColorsValidation);
  @action setColorsValidation = () => {
    this.colorsValidation = cloneDeep(defaultConfigKioskColorsValidation);

    const colorsValidationKeys = Object.keys(this.colorsValidation);
    colorsValidationKeys.shift(); // delete isValid key

    colorsValidationKeys.forEach((key) => {
      let keyValue = this.colorsValidation[key as keyof IConfigKioskColorsValidation] as IValidation;

      keyValue = validationColors(this.colors[key as TConfigKioskColorField], {
        rgb: false,
        html: false,
        hsla: false,
      });

      // reset error text
      keyValue.errorText = undefined;

      (this.colorsValidation[key as keyof IConfigKioskColorsValidation] as IValidation) = keyValue;
      if (!keyValue.isValid) this.colorsValidation.isValid = false;
    });
  };
  @observable imagesValidation: IConfigKioskImagesValidation = cloneDeep(defaultConfigKioskImagesValidation);
  @action setImagesValidation = () => {
    this.imagesValidation = cloneDeep(defaultConfigKioskImagesValidation);

    [this.imagesValidation.bannerImage.isValid, this.imagesValidation.isValid] = emptyStringValidation(
      this.images.bannerImage,
      this.imagesValidation.bannerImage.isValid,
      this.imagesValidation.isValid
    );
  };

  @observable configurationValidation: IConfigKioskConfigurationValidation = cloneDeep(defaultConfigKioskConfigurationValidation);
  @action setConfigurationValidation = () => {
    this.configurationValidation = cloneDeep(defaultConfigKioskConfigurationValidation);

    [this.configurationValidation.accessCode.isValid, this.configurationValidation.isValid, this.configurationValidation.accessCode.errorText] =
      minCharsValidation(this.configuration.accessCode, this.configurationValidation.accessCode.isValid, this.configurationValidation.isValid, 4);
  };

  @action clearStore = () => {
    this.setFocusField(null);
    this.isEdit = false;

    this.colors = this.configKiosk.colors;
    this.images = this.configKiosk.images;
    this.configuration = this.configKiosk.configuration;

    this.colorsValidation = cloneDeep(defaultConfigKioskColorsValidation);
    this.configurationValidation = cloneDeep(defaultConfigKioskConfigurationValidation);
  };
}

const configKioskStore = new ConfigKioskStore();

export default configKioskStore;
